import React, { useState, useEffect, Suspense } from 'react';
import { NavigationContainer, useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { PaperProvider } from 'react-native-paper';
import { Provider } from 'react-redux';
import { AutocompleteDropdownContextProvider } from 'react-native-autocomplete-dropdown';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import { Platform } from 'react-native';

import { initSentry } from './src/config/sentry.config';
import store from './src/store/store';
import { UserAuth } from './src/types/users/User';
import { UserAuthInitialValues } from './src/types/users/interfaces';

import RoutesProtected from './routesProtected';
import LoginScreen from './src/screens/LoginScreen';

const ErrorFallback = () => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <h2>Ha ocurrido un error</h2>
    <p>Por favor, intenta recargar la página</p>
  </div>
);

const App = () => {
  let page = '/';

  if (Platform.OS === 'web') {
    page = window.location.pathname + window.location.search;
  }

  ReactGA.initialize('G-BHC2HBMDHH');
  ReactGA.send({
    hitType: 'pageview',
    page,
    title: 'POS',
  });

  try {
    initSentry();
  } catch (error) {
    console.error('Error initializing Sentry:', error);
  }

  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<UserAuth>(UserAuthInitialValues);
  
  AsyncStorage.getItem('user').then((data) => {
    if (data !== null) {
      setAuthenticated(true);
    }
  });

  useEffect(() => {
    AsyncStorage.getItem('user').then((data) => {
      if (data !== null) {
        setUser(JSON.parse(data));
      }
    });
  }, [authenticated]);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      if (authenticated) {
        const style = document.createElement('style');
        style.id = 'hide-recaptcha-badge';
        style.textContent = `
          .grecaptcha-badge {
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(style);
      } else {
        const existingStyle = document.getElementById('hide-recaptcha-badge');
        if (existingStyle) {
          existingStyle.remove();
        }
      }
    }
  }, [authenticated]);

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <AutocompleteDropdownContextProvider>
        <PaperProvider>
          <Provider store={store}>
            <NavigationContainer documentTitle={{formatter: (options) => options?.title || 'MOZZOPOS'}}>
              {authenticated ? (
                <RoutesProtected
                  setAuthenticated={setAuthenticated}
                  user={user}
                  authenticated={authenticated}
                />
              ) : (
                <LoginScreen
                  setAuthenticated={setAuthenticated}
                  navigation={useNavigation}
                />
              )}
            </NavigationContainer>
          </Provider>
        </PaperProvider>
      </AutocompleteDropdownContextProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
